import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { palette } from "../../../assets/styles/Palette";
import { Button } from "../../../assets/styles/ButtonStyle";
import PopupWrap from "../../../assets/styles/Popup";
import {
  ContentsWrap,
  MainContent,
  TabContent5,
  TabContent5Item,
} from "../../../assets/styles/BusinessAnalysisStyle";
import {
  H3,
  Body1,
  Body2,
  Body3,
  InputText,
} from "../../../assets/styles/Typography";
import {
  FormBox,
  CustomTextarea,
} from "../../../assets/styles/InputStyle";
import { useDynamicViewport } from "../../../assets/DynamicViewport";
import OrganismDesignConceptSelector from "../components/organisms/OrganismDesignConceptSelector";
import MoleculeAnalysisCard from "../components/molecules/MoleculeAnalysisCard";
import MoleculeEditableMarkdown from "../components/molecules/MoleculeEditableMarkdown";
import MoleculeCreateDisplayHeader from "../components/molecules/MoleculeCreateDisplayHeader";
import {
  IS_LOGGED_IN,
  AUTODISPLAY_SELECTED_DEVICE,
  CUSTOMER_ID,
} from "../../AtomStates";
import { useAtom } from "jotai";
import {
  createInfoBoardContentOnServer,
  updateInfoBoardContentOnServer,
  getUserMemberType,
} from "../../../utils/indexedDB";

const designConceptsData = [
  {
    id: "concept1",
    name: "컨셉1",
    description: "컨셉1",
  },
];

const PageCreateInfoCustomContent = () => {
  const navigate = useNavigate();

  const [isLoggedIn] = useAtom(IS_LOGGED_IN);
  const [selectedDevice] = useAtom(AUTODISPLAY_SELECTED_DEVICE);
  const [customerId, setCustomerId] = useAtom(CUSTOMER_ID);

  // UI 상태 관리
  const [showPopupError, setShowPopupError] = useState(false);
  const [contentTitle, setContentTitle] = useState("");
  const [contentSource, setContentSource] = useState("");
  const [contentMessage, setContentMessage] = useState("");
  const [selectedDesignConcepts, setSelectedDesignConcepts] = useState([]);
  const [markdown, setMarkdown] = useState(["", ""]);
  const [title, setTitle] = useState([""]);
  const [isEditingMarkdown, setIsEditingMarkdown] = useState(true); // 처음부터 편집 모드
  const [isMarkdownSaved, setIsMarkdownSaved] = useState(false); // 마크다운 저장 여부
  const [activeTab, setActiveTab] = useState(1); // 현재 활성 탭
  const [completedSteps, setCompletedSteps] = useState([]); // 완료된 단계
  const [savedViewId, setSavedViewId] = useState(null); // 저장된 viewId
  const [timeStamp, setTimeStamp] = useState(null);
  const [contentTarget, setContentTarget] = useState("전체 사용자");

  useDynamicViewport("width=1280");

  useEffect(() => {
    window.scrollTo(0, 0);
    const timeStamp = new Date().getTime();
    setTimeStamp(timeStamp);
  }, []);

  useEffect(() => {
    const checkMemberType = async () => {
      const accessToken = sessionStorage.getItem("accessToken");
      const userMemberType = await getUserMemberType(accessToken);
      const lastUrl = sessionStorage.getItem("lastUrl");

      if (
        (lastUrl === "/CreateApiContent" || lastUrl === "/ApiDashBoard") &&
        userMemberType.member_type === 99
      ) {
        navigate("/Admin");
      }

      if (userMemberType.member_type === 11) {
        navigate("/DashBoard");
      } else if (userMemberType.member_type === 12) {
        navigate("/CustomerManage");
      } else if (userMemberType.member_type === 21) {
        navigate("/ApiDashBoard");
      } else if (userMemberType.member_type === 22) {
        navigate("/CustomerManage");
      }
    };

    checkMemberType();
  }, []);

  const handleDesignConceptSelect = (conceptId) => {
    setSelectedDesignConcepts((prev) => {
      if (prev.includes(conceptId)) {
        return [];
      } else {
        return [conceptId];
      }
    });
  };

  const handleSaveMarkdown = (data) => {
    // editableTitle이 true인 경우 data는 { markdown, title } 객체
    // false인 경우 data는 markdown 배열
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      setMarkdown(data.markdown || ["", ""]);
      // 타이틀을 항상 배열 형태로 저장
      if (data.title) {
        if (Array.isArray(data.title)) {
          setTitle(data.title);
        } else {
          // 문자열이면 배열로 변환: ["입력한 제목"]
          setTitle([data.title]);
        }
      } else {
        // 타이틀이 없으면 빈 배열
        setTitle([""]);
      }
      // 마크다운이 저장되었는지 확인 (최소한 하나의 섹션이 비어있지 않으면 저장된 것으로 간주)
      const savedMarkdown = data.markdown || ["", ""];
      const hasContent = savedMarkdown.some(section => section && section.trim() !== '');
      setIsMarkdownSaved(hasContent);
    } else {
      setMarkdown(data || ["", ""]);
      // 마크다운이 저장되었는지 확인
      const savedMarkdown = data || ["", ""];
      const hasContent = savedMarkdown.some(section => section && section.trim() !== '');
      setIsMarkdownSaved(hasContent);
    }
  };

  const handleCancelMarkdown = () => {
    // 취소 시에는 저장 상태를 변경하지 않음
    // 저장된 내용이 있으면 그 상태를 유지하고, 없으면 false 상태 유지
  };

  const handleSaveContent = async () => {
    // 필수 항목 체크
    if (!contentTitle || contentTitle.trim() === '') {
      setShowPopupError(true);
      throw new Error("콘텐츠 제목을 입력해주세요");
    }

    if (selectedDesignConcepts.length === 0) {
      setShowPopupError(true);
      throw new Error("디자인 테마를 선택해주세요");
    }

    try {
      let viewId = savedViewId;
      
      // 아직 저장되지 않은 경우 create, 이미 저장된 경우 update
      if (!viewId) {
        viewId = await createInfoBoardContentOnServer(isLoggedIn);
        setSavedViewId(viewId);
      }

      // 데이터를 조합하여 contentData 객체 생성
      const now = new Date();
      const contentData = {
        viewDevice: {
          _id: selectedDevice?._id || "",
          deviceTitle: selectedDevice?.deviceTitle || "",
          deviceDPI: selectedDevice?.deviceSpec?.deviceDPI || "",
          deviceInch: selectedDevice?.deviceSpec?.deviceInch || "",
          deviceCm: selectedDevice?.deviceSpec?.deviceCm || "",
          deviceWidth: selectedDevice?.deviceSpec?.deviceWidth || "",
          deviceHeight: selectedDevice?.deviceSpec?.deviceHeight || "",
        },
        uploadFileData: [],
        textData: "",
        dataAnalysis: {
          title: contentTitle,
          source: contentSource,
          message: contentMessage,
          detail: markdown.join("\n\n"),
        },
        designConcept: selectedDesignConcepts[0],
        contentsType: "custom",
        contentsTarget: contentTarget,
        contentGeneratorMarkdown: markdown, // 배열 형태: ["섹션1", "섹션2"]
        contentGeneratorJson: null,
        contentGeneratorTitle: title.length > 0 && title[0] ? title : [contentTitle], // 배열 형태: ["타이틀"]
        createDate: now.toLocaleString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
        createTimestamp: now.getTime(),
      };
      
      if (customerId !== "") {
        contentData.customer = customerId;
      }

      await updateInfoBoardContentOnServer(viewId, contentData);
      
      return viewId;
    } catch (error) {
      console.error("콘텐츠 저장 실패:", error);
      setShowPopupError(true);
      throw error; // 에러를 다시 throw하여 호출자에게 전달
    }
  };

  const handleConfirmContent = async () => {
    // 2단계에서는 이미 저장된 데이터를 확인만 함
    // 필요시 최종 업데이트를 수행할 수 있음
    try {
      if (savedViewId) {
        // 이미 저장된 경우 최종 업데이트
        await handleSaveContent();
      } else {
        // 저장되지 않은 경우 저장
        await handleSaveContent();
      }
    } catch (error) {
      console.error("콘텐츠 확인 또는 업데이트 실패:", error);
      setShowPopupError(true);
    }
  };

  useEffect(() => {
    // 새로고침 감지 함수
    const detectRefresh = () => {
      const currentUrl = window.location.href;
      if (currentUrl.toLowerCase().includes("createinfocustomcontent")) {
        const lastUrl = sessionStorage.getItem("lastUrl");

        if (lastUrl && lastUrl === currentUrl) {
          navigate("/InfoContentManageDashBoard");
          return true;
        }

        sessionStorage.setItem("lastUrl", currentUrl);
      }

      return false;
    };

    // F5 키 또는 Ctrl+R 감지
    const handleKeyDown = (event) => {
      if (
        (event.key === "r" && (event.metaKey || event.ctrlKey)) ||
        event.key === "F5"
      ) {
        event.preventDefault();
        navigate("/InfoContentManageDashBoard");
      }
    };

    detectRefresh();
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate]);

  const handleGoBack = async () => {
    navigate("/InfoContentManageDashBoard");
  };

  // 분석 데이터 객체 생성 (미리보기용)
  const analysisData = {
    title: contentTitle || "제목",
    detail: markdown.join("\n\n"),
  };

  return (
    <>
      <ContentsWrap>
        <MoleculeCreateDisplayHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          completedSteps={completedSteps}
          isLoading={false}
          onSubmitConcept={async () => {
            // 1단계에서 "다음" 버튼 클릭 시 서버에 저장
            await handleSaveContent();
            setCompletedSteps([1]); // 1단계 완료 표시
            setActiveTab(2);
          }}
          onSubmitReport={() => {}}
          onDisplayConversion={() => {}}
          hasContentDraft={true}
          onConfirmContent={handleConfirmContent}
          isLoadingTemplate={false}
          canProceedFromStep1={
            !isEditingMarkdown && 
            contentTitle.trim() !== '' && 
            isMarkdownSaved &&
            selectedDesignConcepts.length > 0
          }
          canProceedFromStep2={true}
          canProceedFromStep3={
            !isEditingMarkdown && 
            contentTitle.trim() !== '' && 
            selectedDesignConcepts.length > 0 &&
            isMarkdownSaved
          }
          customSteps={[
            { number: 1, label: "콘텐츠 작성", activeTab: 1 },
            { number: 2, label: "디자인 선택 및 확정", activeTab: 2, requiredCompletedStep: 1 },
          ]}
        />

        <MainContent Wide1030>
          <DesignAnalysisWrap>
            {activeTab === 1 && (
              <TabContent5>
                <div className="content">
                  <TabContent5Item required>
                    <div style={{ height: "20px" }}></div>
                    <H3
                      color="gray800"
                      style={{ textAlign: "left", marginBottom: "24px" }}
                    >
                      커스텀 콘텐츠를 직접 작성해주세요
                    </H3>

                  {/* 좌우 레이아웃: 편집 영역과 미리보기 */}
                  <CustomContentLayout>
                    {/* 왼쪽: 편집 영역 */}
                    <EditSection>
                      <ContentInputSection>
                        <InputGroup>
                          <Label>콘텐츠 제목</Label>
                          <FormBox Large>
                            <CustomTextarea
                              Edit
                              rows={1}
                              placeholder="콘텐츠 제목을 입력해주세요"
                              value={contentTitle}
                              onChange={(e) => setContentTitle(e.target.value)}
                              status="valid"
                              maxLength={50}
                            />
                          </FormBox>
                        </InputGroup>
                      </ContentInputSection>

                      <div
                        className="title"
                        style={{ marginTop: "32px", marginBottom: "16px" }}
                      >
                        <Body1 color="gray700">
                          디스플레이 콘텐츠 내용
                        </Body1>
                      </div>

                      <MoleculeEditableMarkdown
                        markdown={markdown}
                        title={title}
                        onSave={handleSaveMarkdown}
                        onRegenerate={() => {}}
                        onEditingChange={setIsEditingMarkdown}
                        onCancel={handleCancelMarkdown}
                        maxChars={300}
                        showRegenerateButton={false}
                        editableTitle={true}
                        initialEditing={true}
                        placeholder={{
                          title: "콘텐츠 제목을 입력하세요",
                          section1: "**소제목1**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요 \n\n **소제목2**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요 \n\n **소제목3**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요",
                          section2: "**소제목1**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요 \n\n **소제목2**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요 \n\n **소제목3**\n 세부 내용을 여기에 적어주세요\n 여기에 적어주세요",
                        }}
                      />

                      {/* 디자인 테마 선택 */}
                      <div
                        className="title"
                        style={{ marginTop: "32px", marginBottom: "16px" }}
                      >
                        <H3
                          color="gray800"
                          style={{ textAlign: "left" }}
                        >
                          디자인 테마를 선택해주세요
                        </H3>
                      </div>
                      <OrganismDesignConceptSelector
                        concepts={designConceptsData}
                        selectedConcepts={selectedDesignConcepts}
                        onSelect={handleDesignConceptSelect}
                        disabled={false}
                        titleData={title}
                        markdownData={markdown}
                      />
                    </EditSection>

                  </CustomContentLayout>
                  </TabContent5Item>
                </div>
              </TabContent5>
            )}

            {activeTab === 2 && (
              <TabContent5>
                <div className="content">
                  <TabContent5Item required>
                    <H3
                      color="gray800"
                      style={{ textAlign: "left", marginBottom: "-20px" }}
                    >
                      확정된 콘텐츠를 화면에 적용해보세요
                    </H3>
                    <div style={{ marginTop: "32px", marginBottom: "32px", height: "1px", borderTop: "2px solid #E0E4EB" }}></div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px", margin: "32px auto", textAlign: "center", width: "100%" }}>
                      <div style={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "#226FFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg
                          width="28"
                          height="26"
                          viewBox="0 0 28 26"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M26.7408 1.07458C27.9568 1.9055 28.269 3.56483 27.438 4.78081L13.7714 24.7808C13.3237 25.4359 12.6076 25.8576 11.8176 25.9314C11.0277 26.0052 10.2458 25.7234 9.68463 25.1625L1.34609 16.8292C0.304367 15.7881 0.30384 14.0997 1.34491 13.0579C2.38599 12.0162 4.07443 12.0157 5.11615 13.0568L11.1813 19.1181L23.0346 1.77179C23.8655 0.555813 25.5249 0.243661 26.7408 1.07458Z"
                            fill="white"
                          />
                        </svg>
                      </div>
                      <div style={{ textAlign: "center", maxWidth: "400px" }}>
                        <H3
                          color="gray800"
                          style={{
                            marginBottom: "8px",
                            marginTop: "16px",
                          }}
                        >
                          콘텐츠 등록이 완료되었습니다
                        </H3>
                      </div>
                    </div>
                    <div style={{ marginTop: "32px", marginBottom: "32px", height: "1px", borderTop: "2px solid #E0E4EB" }}></div>
                  </TabContent5Item>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 0" }}>
                  <Button
                    Primary
                    Large
                    Fill
                    style={{
                      width: "120px",
                      height: "40px",
                      fontSize: "16px",
                    }}
                    onClick={handleGoBack}
                  >
                    돌아가기
                  </Button>
                </div>
              </TabContent5>
            )}
          </DesignAnalysisWrap>
        </MainContent>
      </ContentsWrap>

      {showPopupError && (
        <PopupWrap
          Warning
          title="입력 오류"
          message="콘텐츠 제목과 디자인 테마는 필수 항목입니다."
          buttonType="Outline"
          confirmText="확인"
          isModal={false}
          onConfirm={() => setShowPopupError(false)}
        />
      )}
    </>
  );
};

export default PageCreateInfoCustomContent;

const DesignAnalysisWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 100px;
  margin-top: 60px;
`;

const CustomContentLayout = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 1400px) {
    flex-direction: column;
  }
`;

const EditSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const PreviewSection = styled.div`
  flex: 0 0 400px;
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1400px) {
    flex: 1;
    width: 100%;
    position: static;
  }
`;

const PreviewTitle = styled.div`
  margin-bottom: 8px;
`;

const PreviewContentBox = styled.div`
  border: 1px solid ${palette.outlineGray};
  border-radius: 10px;
  padding: 20px;
  background: white;
`;

const ContentInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 0px;
  margin-bottom: 24px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Label = styled.label`
  color: #666666;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 1.55;
  text-align: left;
`;

const ContentAnalysisSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin: 24px 0 0px 0;
  width: 100%;
`;

const DashedLine = styled.div`
  width: 100%;
  height: 1px;
  border-top: 2px solid ${palette.gray300};
  margin: 0px;
  margin-top: 32px;
  margin-bottom: 36px;
`;

const CompletionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 32px auto;
  text-align: center;
  width: 100%;
`;

const CompletionIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${palette.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const CompletionMessage = styled.div`
  text-align: center;
  max-width: 400px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;
